import { Row, Card, Typography } from 'antd'
import { EmptyHeaderHero, SEO } from './components'

import PrivacyStyle from '../../styles/PrivacyNotice.module.css'

const { Title } = Typography

const PrivacyNotice = () => {
  return (
    <>
      <SEO
        title={"Privacy Notice | CURE Auto Insurance"} 
        description={"CURE auto insurance’s Privacy Notice. Learn how we collect, use and protect your personal information to ensure transparency and data security."} 
        url={"/privacy-notice"}
      />
      <EmptyHeaderHero title="Privacy Notice" />
      <div className={`${PrivacyStyle["privacy-notice-screen"]}`}>
        <Row>
          <Card className={`${PrivacyStyle["privacy-notice-card"]}`}>

            <article>
              <div className={`${PrivacyStyle["privacy-notice-mobile-container"]}`}>
                <div className={`${PrivacyStyle["privacy-notice-mobile"]}`}>
                  <a className={`${PrivacyStyle["link"]}`} href="/optout" target="_blank">
                    <Title level={3} style={{ margin: 12, height: '100%' }}>
                      Call Recording Sharing Opt-Out
                    </Title>
                  </a>
                </div>
              </div>
              <div className={`${PrivacyStyle["privacy-notice-desktop"]}`}>
                <a className={`${PrivacyStyle["link"]}`} href="/optout" style={{ height: '100%' }} target="_blank">
                  <Title level={3} style={{ margin: 12, height: '100%' }}>
                    Call Recording Sharing Opt-Out
                  </Title>
                </a>
              </div>
              <div>
                <h5>OVERVIEW/PURPOSE OF THE NOTICE</h5>
                <p>
                  This section provides an overview of our privacy practices. We encourage you to read the full Privacy Notice in addition to this 
                  overview.
                </p>
              </div>
              <p>
                This Privacy Notice is effective immediately and describes how Citizens United Reciprocal Exchange (“CURE”) and its affiliates 
                (collectively, “CURE,” “we,” “our” or “us”) may protect, collect, use, disclose or otherwise process your personal data in 
                connection with the services offered through CURE, its website, application or by any other means (collectively, the “Services”). 
                You accept and agree to the terms of this Privacy Notice through using our services. 
              </p>
              <p>
                CURE respects your personal privacy and strives to protect it. We also understand the highly sensitive nature of the personal 
                data you are providing and believe that earning your trust must be a high priority. We consider it of the utmost importance to 
                safeguard this information in order to provide a positive and personalized insurance experience.
              </p>
              <p>
                For purposes of this notice, personal data shall broadly include information that is linked or reasonably linkable to an identified 
                or identifiable person. It does not include any aggregate, anonymized, de-identified data. Sensitive personal data means personal data 
                revealing racial or ethnic origin; religious beliefs; mental or physical health condition, treatment, or diagnosis; financial 
                information, which shall include a consumer’s account number, account log-in, financial account, or credit or debit card number, in 
                combination with any required security code, access code, or password that would permit access to a consumer’s financial account; 
                sex life or sexual orientation; citizenship or immigration status; status as transgender or non-binary; genetic or biometric data that 
                may be processed for the purpose of uniquely identifying an individual; personal data collected from a known child; or precise geolocation 
                data.
              </p>
            </article>

            <h5>WHY COLLECT INFORMATION AND HOW DO WE USE IT?</h5>
            <p>
              The information you provide to CURE is necessary for it to provide insurance to you. It uses your personal data no longer than is necessary for the 
              purpose to generate accurate rate quotes and help service your policy. This may be to do the following: 
            </p>
            <ul style={{ listStyle: 'disc', marginLeft: 24, marginBottom: '.7em' }}>
              <li>Create your account/policy</li>
              <li>Confirm your identity</li>
              <li>Offer insurance services</li>
              <li>Account/policy administration and review</li>
              <li>Determine your eligibility</li>
              <li>Process your application for coverage</li>
              <li>Process your claims</li>
              <li>Investigate claims</li>
              <li>Process payments</li>
              <li>Respond to your inquires and requests</li>
              <li>Administer products and services to you</li>
              <li>Personalize your services and experiences on its website/application</li>
              <li>Protect against fraud and unauthorized experiences</li>
              <li>Improve services and customer interactions and experiences</li>
              <li>To improve business, enhancing services</li>
              <li>To provide customer service</li>
              <li>Develop and offer new services</li>
              <li>Provide information to you</li>
              <li>Perform research</li>
              <li>Identify, prevent and protect against fraud, unauthorized transactions and any other unlawful activity, claims and/or other liabilities</li>
              <li>To comply with and enforce applicable legal requirements, relevant industry standards, contractual obligations and our 
                policies, including this Privacy Notice</li>
              <li>Perform other activities as required or permitted under law</li>
            </ul>

            <h5>DATA RETENTION</h5>
            <p>
              CURE is licensed to do business in New Jersey, Pennsylvania and Michigan. It retains the personal data of its consumers based on the insurance regulations 
              governing the minimum retention period of these states. Personal data may be retained for a longer period if required under other applicable federal or state 
              laws.
            </p>

            <h5>WHAT DO WE COLLECT?</h5>
            <p>
              CURE only collects personal data to what is adequate, relevant and reasonably necessary for and compatible with the purposes for which we process such data. 
              The personal data we process may include the following categories:
            </p>

            <table className={`${PrivacyStyle["privacy-notice-table"]} ${PrivacyStyle["privacy-notice-table-desktop"]}`} style={{marginBottom:'1em'}}>
              <tbody>
                <tr className={`${PrivacyStyle["border-bottom"]}`}>
                  <td colspan="2">
                    <p className={`${PrivacyStyle["privacy-notice-table-title"]}`}>
                      Sensitive Personal Data
                    </p>
                  </td>
                </tr>
                <tr className={`${PrivacyStyle["border-bottom"]}`}>
                  <td className={`${PrivacyStyle["border-right"]}`} style={{width:'300px'}}>
                    <p><strong>Contact information</strong></p>
                  </td>
                  <td className={`${PrivacyStyle["content"]}`}>
                    <p>Names, addresses, home telephone numbers, fax numbers and e-mail addresses.</p>
                  </td>
                </tr>
                <tr className={`${PrivacyStyle["border-bottom"]}`}>
                  <td className={`${PrivacyStyle["border-right"]}`}>
                    <p><strong>Account information</strong></p>
                  </td>
                  <td className={`${PrivacyStyle["content"]}`}>
                    <p>Usernames and passwords.</p>
                  </td>
                </tr>
                <tr className={`${PrivacyStyle["border-bottom"]}`}>
                  <td className={`${PrivacyStyle["border-right"]}`}>
                    <p><strong>Marketing preferences</strong></p>
                  </td>
                  <td className={`${PrivacyStyle["content"]}`}>
                    <p>Your interactions and responses to our marketing communications.</p>
                  </td>
                </tr>
                <tr className={`${PrivacyStyle["border-bottom"]}`}>
                  <td className={`${PrivacyStyle["border-right"]}`}>
                    <p><strong>Financial information</strong></p>
                  </td>
                  <td className={`${PrivacyStyle["content"]}`}>
                    <p>Credit card or debit card numbers to process payment, which are encrypted in transit and masked at rest.</p>
                  </td>
                </tr>
                <tr className={`${PrivacyStyle["border-bottom"]}`}>
                  <td className={`${PrivacyStyle["border-right"]}`}>
                    <p><strong>Other personally identifiable information:</strong></p>
                  </td>
                  <td className={`${PrivacyStyle["content"]}`}>
                    <p>Citizenship, immigration status, medical information related to litigation claims, geolocation data, dates of birth, 
                    last four of Social Security numbers, government identification such as driver’s license and military identification, 
                    credit information, and motor vehicle records.</p>
                  </td>
                </tr>
              </tbody>
            </table>

            <p className={`${PrivacyStyle["privacy-notice-table-title"]} ${PrivacyStyle["privacy-notice-table-mobile"]}`}>
              Sensitive Personal Data
            </p>
            <table className={`${PrivacyStyle["privacy-notice-table"]} ${PrivacyStyle["privacy-notice-table-mobile"]}`} style={{marginBottom:'1em'}}>
              <tbody>
                <tr>
                  <td className={`${PrivacyStyle["border"]}`}>
                    <p>
                      <strong>Contact information</strong><br />
                      Names, addresses, home telephone numbers, fax numbers and e-mail addresses.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td className={`${PrivacyStyle["border"]}`}>
                    <p>
                      <strong>Account information</strong><br />
                      Usernames and passwords.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td className={`${PrivacyStyle["border"]}`}>
                    <p>
                      <strong>Marketing preferences</strong><br />
                      Your interactions and responses to our marketing communications.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td className={`${PrivacyStyle["border"]}`}>
                    <p>
                      <strong>Financial information</strong><br />
                      Credit card or debit card numbers to process payment, which are encrypted in transit and masked at rest. 
                    </p>
                  </td>
                </tr>
                <tr>
                  <td className={`${PrivacyStyle["border"]}`}>
                    <p>
                      <strong>Other personally identifiable information:</strong><br />
                      Citizenship, immigration status, medical information related to litigation claims, geolocation data, dates of birth, 
                      last four of Social Security numbers, government identification such as driver’s license and military identification, 
                      credit information, and motor vehicle records.
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Additional Data Collected</p>
            <p>
              We may also collect information pertaining to the marital status of the applicant and the principal driver of each automobile to be insured, 
              and information pertaining to the vehicle(s) to be insured, including how each vehicle is used. Information regarding licensed drivers to be 
              insured, regular operators of your vehicle and other members of your household will also be obtained, including driving records and claims history 
              information. Personal data may be collected for persons other than the individual or individuals proposed for coverage, such as other household 
              members or vehicle operators who are not named insureds under a separate automobile insurance policy. Additionally, by applying for a CURE auto policy, 
              you are giving us permission to confirm your motor vehicle record and claims history. 
            </p>
            <p>
              As such, we may also collect information from our records and from other authorized providers of personal data. We order a copy of your motor vehicle 
              record and may also seek information from authorized providers of claims information. These providers check to see if you have filed auto claims with 
              other insurers. We may also obtain credit information where permitted by law. Although CURE does not use credit reports, occupation, or education level 
              when underwriting a policy, CURE may use this information for the purpose of marketing and research. Some of CURE’s business partners may use this 
              information to provide you with related services. Our sales and service representatives do not have access to the details of your credit information. 
              This inquiry will not affect your credit score or credit rating. Other companies who view your credit report will not see the inquiry. It will be visible 
              only to you. No sensitive personal data will be processed by CURE without first obtaining your consent. 
            </p>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>IP Addresses and Cookies</p>
            <p>
              By using our services, we may also obtain certain information by automated means, including your device IP address, domain name, browser cookies and 
              identifiers associated with your device. We use this information for analytics, marketing, and functionality to enhance user experience and offer you 
              optimal service. Cookies and other similar tracking mechanisms identify you to the website that gave you the cookie. It is a small text file saved in 
              your computer when you access certain web pages.
            </p>
            <p>
              CURE may use cookies if you request a rate quote from us. We do not use cookies to store or transmit any personal data about you. The use of cookies may, 
              however, permit us to identify a visitor if the visitor has also provided identifying information. If you have set your browser to warn you before accepting 
              cookies, you should receive a warning message that will allow you to refuse cookies. CURE’s website requires cookies in order to process a rate quote.
            </p>
            <p>
              As with most websites, CURE can also detect and store your IP address and that of the website that referred you to us. We collect such information to 
              determine web traffic and usage patterns and use such information to help provide adequate capacity for our visitors to this website. Please be aware we 
              are not able to determine your e-mail address or any other personal data unless you provide it to us. 
            </p>

            <h5>WHEN AND WITH WHOM DO WE DISCLOSE YOUR INFORMATION TO?</h5>
            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Data Sharing with Third Parties</p>
            <p>
              CURE will only disclose your personal data where permitted or required by law. CURE will not sell personal data about you to non-affiliated companies 
              other than to certain third-party institutions, such as insurance institutions, with whom we have joint marketing agreements, in order to market products 
              and services that we and they offer. CURE does not sell customer information to mailing list companies or telemarketing companies unless the customer 
              resides outside of the states in which CURE is licensed to offer its insurance products. Consumers that reside outside of these states have the right to 
              opt-out of CURE selling such information. Please see “Your Rights and Choices” below for more information on opting out. 
            </p>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Customer Call Center Data</p>
            <p>
              CURE may record or monitor calls to its call center for quality assurance and training purposes in compliance with applicable federal and state laws. 
              If you do not consent to being recorded, at the beginning of the call, you may express that you do not consent, or you may immediately disconnect from the 
              call. By continuing with the call, you acknowledge and agree to being recorded.  
            </p>
            <p>
              CURE may also sell or share a portion of the call recording, including voice biometric data, with third parties for AI training purposes. CURE is only 
              licensed to do business in New Jersey, Pennsylvania and Michigan and only collects consumer information for these states. All sensitive or personally 
              identifiable information (or non-public personal information) disclosed during any calls that are affiliated with the consumer will not be shared and 
              shall be removed or redacted from the call recording. 
            </p>
            <p>
              If you do not consent to the selling or sharing of the call recording for this stated purpose, you may verbally inform the call representative or agent 
              at the beginning of the call that you would like to opt-out and you may also visit <span style={{textDecoration: "underline"}}>cure.com/optout</span> to 
              exercise your right to opt-out at any time. By continuing with the call, you acknowledge and agree to the call recording being shared for the above stated 
              purpose. 
            </p>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Joint Marketing Agreements with Third Parties</p>
            <p>
              We may also enter into joint marketing agreements with other companies or request other companies to perform marketing services on our behalf. We strive 
              to ensure that our business partners support our commitment to privacy protection in their handling of personal data about our policyholders. If you are 
              a customer, we do not sell your information without your consent. However, if you have requested an on-line insurance quote from us, we may sell the 
              information you provided us, such as your email address, to companies with whom we have joint marketing agreements so that they may follow up with you with 
              respect to your CURE application, or so that they can provide you with additional quotes from those companies or the companies that they represent. 
            </p>
            <p>
              As required by law, all companies participating in this program must sign a joint marketing agreement prohibiting the company from using your information 
              for any other purpose unless you choose to purchase a policy from or through them or otherwise become their customer. Examples where disclosure is permitted:
            </p>
            <ul style={{ listStyle: 'disc', marginLeft: 24, marginBottom: '.7em' }}>
              <li>Service companies that help CURE issue or maintain policies and handle claims, such as those who appraise vehicle value, investigate and settle claims or 
                determine eligibility for benefits or payment.</li>
              <li>Companies that have entered into joint marketing agreements with CURE.</li>
              <li>Legal and regulatory authorities. This may involve detection or prevention of criminal acts or fraud.</li>
              <li>Other insurance companies or insurance support organizations in connection with an insurance transaction involving you.</li>
              <li>Agents or agencies that provide insurance products.</li>
              <li>Various databases developed or used by the insurance industry.</li>
              <li>Companies who provide various consumer reports as permitted by law.</li>
            </ul>

            <h5>HOW WE SAFEGUARD YOUR INFORMATION?</h5>
            <p>
              CURE takes reasonable measures to establish, implement, and maintain administrative, technical and physical data security practices in order to 
              protect the confidentiality, integrity, and accessibility of your personal data to secure it from unauthorized acquisition as required by law. These 
              measures include computer safeguards, secured storage, files and buildings. We limit access to your information to our employees and others who must 
              use the information to provide our products and services. We also require any affiliates or third-party companies that work with us to protect your 
              personal data. However, we cannot guarantee the security of any information as unauthorized access, disclosure, alteration or destruction, including 
              software failure or any other variables may compromise the security of user information at any time. 
            </p>
            <p>
              If you provide us with information about someone else, please ensure that you have that person’s permission to do so for purposes detailed in this Privacy 
              Notice. We are not responsible for the collection, usage, and disclosure of policies and practices, including the data security of other organizations for 
              information you disclose to them through or in connection with their sites or applications. 
            </p>
            <p>
              This Privacy Notice does not apply to other website’s information practices. Any website linked to CURE’s website are not ours. We make no warranties or 
              representations about other websites, or the content of any other websites linked to CURE. We encourage you to read the privacy and security policies of 
              any linked sites when visiting them. 
            </p>
            <p>
              In addition to the information contained in this Privacy Notice, our website Security Statement also provides important additional information relating to your 
              use of our website. For more information, please see our Security Statement at 
              <br />
              https://www.cure.com/security-policy.
            </p>

            <h5>YOUR CHOICES AND RIGHTS</h5>
            <p>
              CURE values your privacy. As permitted under law, you have the right to opt-out of the following: 
            </p>
            <ol className={`${PrivacyStyle["privacy-notice-alpha-list"]}`}>
              <li>If you do not reside in New Jersey, Pennsylvania, or Michigan, you may have the right to restrict or opt-out of the collection or 
                processing of your personal data for targeted advertising</li>
              <li>Opt-out of the sale or sharing of your call data sharing with third parties. </li>
            </ol>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Right to Opt-Out</p>
            <p>
              You, or an authorized agent designated to act on your behalf, may click the applicable link below to exercise your right to opt-out of targeted 
              advertising or the selling or sharing of your call data with third parties as indicated above. Subject to applicable federal and state regulations, 
              please be advised that your right to opt-out does not apply to information that is shared with certain third-party providers who perform business 
              functions necessary for us to generate accurate quotes on your behalf or to service your policy. 
            </p>
            <p className={`${PrivacyStyle["privacy-notice-p-left-padding"]}`}>
              <strong>To opt-out of targeted advertising for residents outside of New Jersey, Pennsylvania, or Michigan: <br />
              https://www.cure.com/optout</strong>
            </p>
            <p className={`${PrivacyStyle["privacy-notice-p-left-padding"]}`}>
              <strong>To opt-out of the selling or sharing of your call data with third parties:  https://www.cure.com/optout</strong>
            </p>
            <p>
              We may also deny any opt-out request if we have a good faith, reasonable and documented belief that such a request is fraudulent. We will provide you 
              with notice if we deny such a request based on the belief that it is fraudulent and provide our reason(s) why we believe the request is fraudulent and 
              that we will not comply with the request. 
            </p>
            <p>
              You also have the right to access your personal data and request to review, update, and correct it. All such requests may also be submitted in writing to:
            </p>
            <p className={`${PrivacyStyle["privacy-notice-p-left-padding"]}`}>
              CURE <br />
              Attention: Underwriting Department <br />
              214 Carnegie Center, Suite 301 <br />
              Princeton, New Jersey 08540
            </p>
            <p>
              As permitted under law, you also have the right to do the following: 
            </p>
            <ol className={`${PrivacyStyle["privacy-notice-alpha-list"]}`}>
              <li>Review or access your personal data,</li>
              <li>Update or correct incomplete or inaccurate personal information.</li>
            </ol>
            <p>
              All such requests may also be submitted in writing to CURE to the above-mentioned address. 
            </p>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Right to Review or Access Your Personal Data</p>
            <p>
              For all written requests to review or access, we ask that you please describe the information that you want to access or review and state that your request is in 
              response to our Privacy Notice. Include your full name, address and policy number (if any) in your written request. Within 30 business days, CURE will send you a 
              summary of the information in your file. Any information that has been collected in expectation of or in connection with any claim, civil or criminal proceeding 
              will be excluded. CURE will permit you to review and copy, in person, the recorded personal information pertaining to you or to obtain a copy of the recorded personal 
              information by mail, whichever is preferred.
            </p>

            <p className={`${PrivacyStyle["privacy-notice-subtitle"]}`}>Right to Correct or Update Your Personal Data</p>
            <p>
              CURE takes reasonable steps to ensure the accuracy of your personal data we retain about you. It is your responsibility to ensure you submit true, accurate, 
              and complete information to us, and that you update us in a timely manner if your information changes. For any requests to correct or update your personal 
              data, please let CURE know if the information is incorrect or incomplete. CURE will correct any disputed information that is incorrect or incomplete, taking 
              into account the nature of the information and purposes of the processing of the information. 
            </p>
            <p>
              Once your request has been authenticated and we confirm your identity, we will provide a response within the time frame required by law. If there are any 
              corrections or amendments made, a copy will be provided to you for your record. We may extend the response time where reasonably necessary and permissible 
              under law considering the complexity and number of requests. We will inform you of any such extension and the reason for the extension, including providing 
              the information for all disclosures of personal data that occurred in the prior 12 months where required by law. 
            </p>
            <p>
              Should we review your request and disagree that the disputed information should be corrected or updated, we will advise you in writing the reasons for our 
              refusal. We will also identify any person to whom we have disclosed this personal data within the two years prior to your request. We will also identify the 
              companies that have provided consumer report information about you. If a correction is made, we will also notify anyone who has received the information within 
              the previous two years.
            </p>
            <p>
              If you disagree with the refusal to correct or amend the personal data, you shall be permitted to file a statement with CURE setting forth what you think is the 
              correct, relevant or fair information and a statement of the reasons why you disagree with CUREs refusal to correct, amend or delete the personal information. 
              Your statement will then become part of your file. It will be sent to the same persons to whom we would send a copy of any correction or change. In any subsequent 
              disclosure by CURE or any insurance support organization of the personal information that is subject of disagreement, we will clearly identify the matter or matters 
              in dispute and provide your statement along with the personal information being disclosed.
            </p>
            <p>
              Any information provided by us in response to your requests are provided free of charge, once per consumer during any 12-month period. If we determine that any 
              requests from you are manifestly unfounded, excessive, or repetitive, we may charge you a reasonable fee to cover any administrative costs of complying with 
              the request or decline to act on the request. 
            </p>

            <h5>CHILDREN’S PRIVACY</h5>
            <p>
              In accordance with the Children’s Online Protection Act of 1998 (“COPPA”), CURE does not knowingly collect, use or disclose personal data from children under 
              the age of 13 years old. Should we become aware that a child under the age of 13 has created an account or otherwise provided us with personal data, we will 
              take all reasonable and necessary steps to terminate that account and remove such information. 
            </p>

            <h5>CHANGES TO OUR PRIVACY NOTICE</h5>
            <p>
              CURE may amend this Privacy Notice from time to time and reserves the right to do so. Should we make any changes to the way in which we collect or use information, 
              we will make all reasonable efforts to notify you where required by law by posting those changes at <span style={{textDecoration: "underline"}}>https://www.cure.com</span>. If 
              you are a current policyholder, CURE will provide you with revisions to this Privacy Notice in accordance with applicable state and federal law.
            </p>
            <p>
              You may wish to visit this page periodically to review the most recent Privacy Notice. Your continuing use of this website constitutes your acceptance of any changes 
              or updates to the Privacy Notice, all of which shall become controlling when posted. 
            </p>

            <h5>QUESTIONS/CONTACT US</h5>
            <p>
              Should you have questions about this Privacy Notice, please feel free to write to us at:
            </p>
            <p className={`${PrivacyStyle["privacy-notice-p-left-padding"]}`}>
              CURE <br />
              Attention: Underwriting Department <br />
              214 Carnegie Center, Suite 301 <br />
              Princeton, New Jersey 08540
            </p>
            <hr />
            <p className={`${PrivacyStyle["privacy-notice-foot-note"]}`}>Last Updated: January 28, 2026</p>
          </Card>
        </Row>
      </div>
    </>
  )
}

PrivacyNotice.propTypes = {}

export default PrivacyNotice
